/**
 * Runtime config — populated by Vite at build time from env vars set in the
 * GHA frontend-deploy workflow (which reads them from SSM via the deploy role).
 *
 * Defaults are present so `bun run dev` in a clean checkout doesn't crash; the
 * dev defaults point at the deployed prod resources because there's only one
 * environment (CONSTITUTION R4: single prod stage).
 */

interface RuntimeConfig {
  readonly userPoolId: string;
  readonly userPoolClientId: string;
  readonly userPoolRegion: string;
  readonly apiEndpoint: string;
}

function envOrFail(name: string, fallback?: string): string {
  // Vite exposes env vars prefixed with VITE_ at build time as
  // import.meta.env.VITE_*. Strict-mode TS access via bracket notation.
  const value = (import.meta.env as Record<string, string | undefined>)[name];
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  if (fallback !== undefined) {
    return fallback;
  }
  throw new Error(`Missing required build-time env var: ${name}`);
}

export const config: RuntimeConfig = {
  userPoolId: envOrFail('VITE_USER_POOL_ID', ''),
  userPoolClientId: envOrFail('VITE_USER_POOL_CLIENT_ID', ''),
  userPoolRegion: envOrFail('VITE_USER_POOL_REGION', 'ap-southeast-2'),
  apiEndpoint: envOrFail('VITE_API_ENDPOINT', ''),
};

/** True when the frontend was built with all required env vars. */
export function isConfigured(): boolean {
  return (
    config.userPoolId.length > 0 &&
    config.userPoolClientId.length > 0 &&
    config.apiEndpoint.length > 0
  );
}
